import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { listQueue, removeFromQueue, resetAttempts } from '../offline/db.js';
import type { QueuedItem } from '../offline/db.js';
import { drainNow, onDrain } from '../offline/register.js';
import { MAX_ATTEMPTS } from '../offline/sync.js';
import { useAuth } from '../auth/useAuth.js';
import { useT } from '../i18n/useT.js';

/**
 * What is still sitting on this device, waiting for a network.
 *
 * ## Why it is visible at all
 *
 * A queue nobody can see is indistinguishable from a queue that lost something.
 * The doubt this removes is specific: a person photographs a receipt in a car
 * park with no signal, sees it disappear, and has no way of knowing whether it
 * is safe to throw the paper away.
 *
 * ## Every company's items
 *
 * Shown whatever company is selected, marked when they belong to another one.
 * The queue drains every company, and a notice that listed only the current
 * company's items made receipts captured elsewhere look lost the moment the
 * switcher moved.
 *
 * ## A way out for a stuck item
 *
 * After five refusals the sender stops retrying an item on its own, so one
 * document the server will never accept cannot block every receipt behind it.
 * Deciding what happens next is a person's job, so a stuck item offers both
 * answers: try again (the refusal may have been fixed -- a role given back, a
 * session renewed) or discard it. Discarding asks twice, because the item exists
 * only on this device and is gone for good.
 */
function describe(item: QueuedItem, t: ReturnType<typeof useT>): string {
  return item.payload.kind === 'attachment'
    ? t('offline.itemAttachment', { name: item.payload.filename })
    : t('offline.itemTrip', {
        date: item.payload.date,
        from: item.payload.startAddress,
        to: item.payload.endAddress,
      });
}

export function OfflineQueueNotice(): ReactNode {
  const t = useT();
  const { companyId } = useAuth();
  const [items, setItems] = useState<QueuedItem[]>([]);
  const [sending, setSending] = useState(false);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState<number | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setItems(await listQueue());
    } catch {
      // IndexedDB is unavailable in a private window in some browsers. The
      // queue not existing is a smaller problem than a page that will not
      // render, and everything else on the page works without it.
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Re-read whenever a drain finishes, wherever it was triggered from -- this
    // page, the `online` event, or the service worker's background sync.
    return onDrain(() => {
      void refresh();
    });
  }, [refresh]);

  const send = useCallback((): void => {
    setSending(true);
    setBlocked(null);
    void drainNow()
      .then((result) => {
        setBlocked(result.blocked ?? null);
      })
      .finally(() => {
        setSending(false);
        void refresh();
      });
  }, [refresh]);

  if (items.length === 0) {
    return null;
  }

  const stuck = items.filter((item) => item.attempts >= MAX_ATTEMPTS);

  return (
    <Alert color={stuck.length > 0 ? 'orange' : 'blue'} variant="light">
      <Stack gap="xs">
        <Text fw={500}>{t('offline.queued', { count: items.length })}</Text>
        <Text size="sm">{t('offline.queuedHelp')}</Text>

        {stuck.map((item) => (
          <Stack key={item.id} gap={4}>
            <Text size="sm" c="orange.8">
              {describe(item, t)}
              {item.companyId === companyId ? '' : ` (${t('offline.otherCompany')})`}
              {' — '}
              {t('offline.stuck', { reason: item.lastError ?? '' })}
            </Text>
            <Group gap="xs">
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  void resetAttempts(item.id).then(send);
                }}
              >
                {t('offline.retry')}
              </Button>
              {confirmingDiscard === item.id ? (
                <>
                  <Button
                    size="compact-xs"
                    color="red"
                    onClick={() => {
                      setConfirmingDiscard(null);
                      void removeFromQueue(item.id).then(refresh);
                    }}
                  >
                    {t('offline.discardConfirm')}
                  </Button>
                  <Text size="xs" c="dimmed">
                    {t('offline.discardHelp')}
                  </Text>
                </>
              ) : (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    setConfirmingDiscard(item.id);
                  }}
                >
                  {t('offline.discard')}
                </Button>
              )}
            </Group>
          </Stack>
        ))}

        {blocked !== null && (
          <Text size="sm" c="dimmed">
            {t('offline.blocked', { reason: blocked })}
          </Text>
        )}

        <Group>
          <Button size="compact-sm" variant="light" loading={sending} onClick={send}>
            {sending ? t('offline.sending') : t('offline.sendNow')}
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}
